/**
 * 허밍 «파일로 올리기»(B2) 의 받는 조건 — 화면·`<input accept>`·서버가 **같은 목록**을 봐야 한다.
 *
 * 🔑 서버는 확장자를 믿지 않고 **파일 내용(매직바이트)** 으로 판별한다
 * (`server/src/lib/audio-transcode.ts` `detectAudioMime`: webm·ogg·mp4(m4a)·wav·mpeg(mp3)).
 * 여기 목록은 그 다섯과 1:1 이다. 한쪽만 늘리면 「고를 수는 있는데 올리면 거절되는 파일」이 생긴다.
 */

/** 화면에 적고 `accept` 에 넣는 확장자. 서버 판별 5종과 1:1. */
export const HUMMING_FILE_EXTENSIONS = [".m4a", ".mp3", ".wav", ".webm", ".ogg"] as const;

/** `<input accept>` 값 — 확장자와 MIME 을 함께 준다(기기마다 둘 중 하나만 보는 곳이 있다). */
export const HUMMING_FILE_ACCEPT = [
  ...HUMMING_FILE_EXTENSIONS,
  "audio/mp4",
  "audio/mpeg",
  "audio/wav",
  "audio/webm",
  "audio/ogg",
].join(",");

/** 사람에게 보여줄 한 줄. 서버 상한과 같은 숫자여야 한다. */
export const HUMMING_FILE_HINT =
  `M4A·MP3·WAV·WEBM·OGG, 10MB 이하 · 60초까지`;

/** `server/src/routes/humming.ts` `maxUploadBytes` 와 같은 값. */
export const HUMMING_MAX_BYTES = 10 * 1024 * 1024;

/** `server/src/routes/humming.ts` 의 길이 상한과 같은 값. */
export const HUMMING_MAX_SECONDS = 60;

/**
 * `server/src/routes/humming.ts` `minDurationSeconds` 와 같은 값 — 음악 AI(MiniMax)가 6초보다
 * 짧은 소리를 거절한다(2026-10-08 수업 사고). 녹음을 멈춘 자리에서 먼저 말해 준다.
 */
export const HUMMING_MIN_SECONDS = 6;

export const HUMMING_TOO_SHORT_MESSAGE =
  `${HUMMING_MIN_SECONDS}초 이상 불러 주세요. 음악 AI 는 ${HUMMING_MIN_SECONDS}초보다 짧은 소리를 받지 않아요`;

/**
 * 고른 파일을 보내기 전에 되돌려 준다 — 서버 왕복 없이 그 자리에서 말해 주는 게 낫다.
 * 통과하면 null. 서버는 이보다 엄격하다(내용 판별)므로 이건 «먼저 걸러내는» 문이다.
 */
export function checkHummingFile(file: { name: string; size: number }): string | null {
  const lowered = file.name.toLowerCase();
  const allowed = HUMMING_FILE_EXTENSIONS.some((extension) => lowered.endsWith(extension));
  if (!allowed) {
    return `이 파일 형식은 올릴 수 없어요. ${HUMMING_FILE_EXTENSIONS.join(" · ")} 중에 골라 주세요`;
  }
  if (file.size > HUMMING_MAX_BYTES) {
    return "파일이 너무 커요. 10MB 이하로 올려 주세요";
  }
  if (file.size === 0) {
    return "파일이 비어 있어요. 다른 파일을 골라 주세요";
  }
  return null;
}
